import * as z from "zod";

export const validateEmail = (value: string, required?: boolean): string => {
   const nonEmptySchema = z.string().nonempty("E-postadresse er påkrevd");
   const emailSchema = z.email("E-postadressen er ikke gyldig");

   const schema = required ? z.intersection(nonEmptySchema, emailSchema) : z.union([z.literal(""), emailSchema]);
   const result = schema.safeParse(value);
   return result.error?.issues?.[0]?.message || "";
};

export const validatePhone = (value: string, required?: boolean): string => {
   const requiredTelefonSchema = z
      .string()
      .nonempty({ message: "Telefonnummer er påkrevd" })
      .regex(/^\+?\d{8,}$/, { message: "Telefonnummeret er ikke gyldig" });

   const optionalPhoneSchema = z.union([
      z.string().regex(/^\+?\d{8,}$/, { message: "Telefonnummeret er ikke gyldig" }),
      z.literal("")
   ]);

   const schema = required ? requiredTelefonSchema : optionalPhoneSchema;
   const result = schema.safeParse(value);

   return result.error?.issues?.[0]?.message || "";
};

export const validateNotEmpty = (value: string, required?: boolean) => {
   if (!required) {
      return "";
   }
   const schema = z.string().nonempty({ message: "Feltet kan ikke være tomt" });
   const result = schema.safeParse(value);

   return result.error?.issues?.[0]?.message || "";
};

const wildcardAcceptTypeMapping = { "audio/*": "audio/", "video/*": "video/", "image/*": "image/" } as const;
const wildcardAcceptTypes = Object.keys(wildcardAcceptTypeMapping) as (keyof typeof wildcardAcceptTypeMapping)[];

// https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/accept#unique_file_type_specifiers
const isMatchingAccept = (file: File, accept: string[]) => {
   const fileType = file.type.toLowerCase();
   const fileName = file.name.toLowerCase();
   if (accept.length === 0) {
      return true;
   }
   return accept.some((type) => {
      const lowercaseType = type.toLowerCase();
      if (type.startsWith(".")) {
         return fileName.endsWith(lowercaseType);
      }
      if (wildcardAcceptTypes.includes(lowercaseType as keyof typeof wildcardAcceptTypeMapping)) {
         return fileType.startsWith(wildcardAcceptTypeMapping[lowercaseType as keyof typeof wildcardAcceptTypeMapping]);
      }
      return fileType === lowercaseType;
   });
};

const fileSizeNumberFormat = new Intl.NumberFormat("no-NO", {
   style: "decimal",
   maximumFractionDigits: 2
});

export const createFileUploadValidator =
   (accept: string[], maxSize: number, multiple: boolean) =>
   (value: File[] | File | null, required?: boolean): string => {
      const valueToCheck = Array.isArray(value) && !value.length ? null : value;
      const maxSizeExceededMessageUnit = maxSize < 1024 * 1024 ? "KB" : "MB";
      const maxSizeInUnits = maxSizeExceededMessageUnit === "KB" ? maxSize / 1024 : maxSize / (1024 * 1024);
      const fileUploadSchema = z
         .instanceof(File)
         .refine((file) => file.size > 0, { message: "Feltet kan ikke være tomt" })
         .refine((file) => !maxSize || file.size <= maxSize, {
            message: `Filen kan ikke være større enn ${fileSizeNumberFormat.format(maxSizeInUnits)} ${maxSizeExceededMessageUnit}`
         })
         .refine((file) => isMatchingAccept(file, accept), { message: "Ikke gyldig filtype" });

      if (multiple && valueToCheck) {
         const multipleFileUploadSchema = z
            .array(fileUploadSchema)
            .refine((files) => files.length > 0, { message: "Feltet kan ikke være tomt" });
         const schema = required ? multipleFileUploadSchema : z.union([multipleFileUploadSchema, z.literal(null)]);

         const result = schema.safeParse(valueToCheck);
         return result.error?.issues?.[0]?.message || "";
      }
      const schema = required ? fileUploadSchema : z.union([fileUploadSchema, z.literal(null)]);
      const result = schema.safeParse(valueToCheck);
      return result.error?.issues?.[0]?.message || "";
   };

export const validateFavoriteListName = (value: string, required?: boolean) => {
   const requiredFavoriteListSchema = z
      .string()
      .nonempty({ message: "Det må være mellom 1 og 30 tegn i navnet" })
      .max(30, { message: "Det kan ikke være flere enn 30 tegn i navnet" });

   const optionalFavoriteListSchema = z.union([
      z.string().max(30, { message: "Det kan ikke være flere enn 30 tegn i navnet" }),
      z.literal("")
   ]);

   const schema = required ? requiredFavoriteListSchema : optionalFavoriteListSchema;
   const result = schema.safeParse(value);

   return result.error?.issues?.[0]?.message || "";
};

export const validateNameLength = (value: string, required?: boolean) => {
   const requiredNameSchema = z
      .string()
      .nonempty({ message: "Vennligst oppi navn på mottaker" })
      .max(50, { message: "Det kan ikke være flere enn 50 tegn i navnet" });

   const optionalNameSchema = z.union([
      z.string().max(50, { message: "Det kan ikke være flere enn 50 tegn i navnet" }),
      z.literal("")
   ]);

   const schema = required ? requiredNameSchema : optionalNameSchema;
   const result = schema.safeParse(value);

   return result.error?.issues?.[0]?.message || "";
};

export const validateChecked = (value: boolean, required: boolean) => {
   return !value && required ? "Dette feltet må være avkrysset" : "";
};

export const validateOtpCode = (value: string) => {
   const schema = z
      .string()
      .nonempty({ message: "Vennligst oppgi engangskode" })
      .length(6, { message: "Engangskoden må være 6 siffer" })
      .regex(/^[0-9]+$/, { message: "Engangskoden kan kun inneholde siffer" });

   const result = schema.safeParse(value);
   return result.error?.issues?.[0]?.message || "";
};

export const validatePassword = (value: string) => {
   const schema = z
      .string()
      .nonempty({ message: "Vennligst oppgi passord" })
      .min(8, { message: "Passordet må være minst 8 tegn" });

   const result = schema.safeParse(value);
   return result.error?.issues?.[0]?.message || "";
};

export function useRepeatedPasswordValidator(password: string) {
   return (value: string) => {
      const schema = z.string().refine((val) => val === password, {
         message: "Passordene må være like"
      });
      const result = schema.safeParse(value);
      return result.error?.issues?.[0]?.message || "";
   };
}
